import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Flag SVG components
const RomanianFlag = () => (
  <svg className="w-5 h-4 rounded-sm" viewBox="0 0 30 20">
    <rect width="10" height="20" fill="#002B7F" />
    <rect x="10" width="10" height="20" fill="#FCD116" />
    <rect x="20" width="10" height="20" fill="#CE1126" />
  </svg>
);

const UKFlag = () => (
  <svg className="w-5 h-4 rounded-sm" viewBox="0 0 60 30">
    <clipPath id="t">
      <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/>
    </clipPath>
    <path d="M0,0 v30 h60 v-30 z" fill="#00247d"/>
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#cf142b" strokeWidth="4"/>
    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
    <path d="M30,0 v30 M0,15 h60" stroke="#cf142b" strokeWidth="6"/>
  </svg>
);

export default function LanguageSwitcher({ variant = "dark", bare = false }: { variant?: "dark" | "light"; bare?: boolean }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const light = variant === "light";

  const languages = [
    { code: "ro", label: "Română", Flag: RomanianFlag },
    { code: "en", label: "English", Flag: UKFlag },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-200 ${
          bare
            ? "border border-transparent hover:bg-white/10"
            : light
              ? "border border-slate-200 bg-slate-50 hover:bg-slate-100"
              : "border border-slate-600/50 bg-slate-700/50 hover:bg-slate-700"
        }`}
        aria-label="Change language"
      >
        <currentLang.Flag />
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute right-0 mt-2 w-36 rounded-lg shadow-xl overflow-hidden z-50 border ${
          light ? "bg-white border-slate-200" : "bg-slate-800 border-slate-700"
        }`}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                i18n.language === lang.code
                  ? (light ? "bg-indigo-50 text-indigo-700" : "bg-emerald-600/20 text-emerald-400")
                  : (light ? "text-slate-700 hover:bg-slate-50" : "text-slate-300 hover:bg-slate-700")
              }`}
            >
              <lang.Flag />
              <span>{lang.label}</span>
              {i18n.language === lang.code && (
                <svg className={`w-4 h-4 ml-auto ${light ? "text-indigo-600" : "text-emerald-400"}`} fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
